import { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { MedCampi, MedCmmsCambio, MedCmmsProposta, MedCmmsProposte } from '../../api/types'
import { leggiFoglio, tabellaTsv, type Foglio } from '../../foglio'
import { t } from '../../i18n/lingua'
import { Card, ErrorBanner, StatCard } from '../common'
import { DataTable } from '../DataTable'
import { Finestra } from '../ModificheGitlab'
import { etichetta } from './Finestre'

/**
 * CENSO MED: the clinical engineering list (CMMS) as proposals (D3).
 *
 * The browser reads the Excel or CSV, the Control Plane says what the list
 * says differently from the records, device by device, and the user picks:
 * which devices, which fields, whether wards and sites the master data lacks
 * are created. Nothing is written before «Applica», and applying is the same
 * as writing by hand (same checks). The list is not stored, and never becomes
 * the reference inventory Traffic Analysis measures CENSO against.
 */

type Campo = keyof MedCampi
const LOTTO = 50

function etichettaChiave(k: string): string {
  const e: Record<string, string> = {
    inventory_number: t('numero d’inventario'),
    mac: t('MAC'),
    serial_number: t('numero di serie'),
    ip: t('indirizzo IP'),
    name: t('nome'),
  }
  return e[k] ?? k
}

/** The fields of the proposals, in the order of the record. */
const ORDINE: Campo[] = ['inventory_number', 'serial_number', 'udi_di', 'cnd_code', 'civab_code', 'model',
  'org_unit_id', 'location_id', 'room', 'acceptance_date', 'install_date']

export function ImportCmms() {
  const input = useRef<HTMLInputElement>(null)
  const [fogli, setFogli] = useState<Foglio[] | null>(null)
  const [foglio, setFoglio] = useState(0)
  const [esito, setEsito] = useState<MedCmmsProposte | null>(null)
  const [scelti, setScelti] = useState<Set<string>>(new Set())
  const [campi, setCampi] = useState<Set<Campo>>(new Set(ORDINE))
  const [creaVoci, setCreaVoci] = useState(false)
  const [revisione, setRevisione] = useState(false)
  const [lavoro, setLavoro] = useState<string | null>(null)
  const [fatto, setFatto] = useState<string | null>(null)
  const [erroriApplica, setErroriApplica] = useState<{ device_id: string; error: string }[]>([])
  const [errore, setErrore] = useState<unknown>(null)

  async function carica(file: File) {
    setErrore(null)
    setEsito(null)
    setFatto(null)
    try {
      const letti = await leggiFoglio(file)
      // The sheet with the list: the one with the most rows.
      const indice = letti.reduce((m, f, i) => (f.righe.length > letti[m].righe.length ? i : m), 0)
      setFogli(letti)
      setFoglio(indice)
      await proponi(letti[indice])
    } catch (e) {
      setErrore(e)
    } finally {
      if (input.current) input.current.value = ''
    }
  }

  async function proponi(f: Foglio) {
    setLavoro(t('Confronto con le schede…'))
    setErrore(null)
    try {
      const r = await api.medCmmsProposals(tabellaTsv(f))
      setEsito(r)
      setScelti(new Set(r.proposals.map((p) => p.device_id)))
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(null)
    }
  }

  /** The changes of a proposal that will be applied with the choices made. */
  const daApplicare = (p: MedCmmsProposta): MedCmmsCambio[] =>
    p.changes.filter((c) => campi.has(c.field) && (!c.new_entry || creaVoci))

  const scelte = useMemo(
    () => (esito?.proposals ?? []).filter((p) => scelti.has(p.device_id) && daApplicare(p).length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [esito, scelti, campi, creaVoci],
  )
  const presenti = useMemo(() => ORDINE.filter((k) => (esito?.proposals ?? []).some((p) => p.changes.some((c) => c.field === k))),
    [esito])
  const nuoveVoci = useMemo(() => [...new Set(scelte.flatMap((p) => p.changes.filter((c) => c.new_entry && campi.has(c.field))
    .map((c) => c.proposed)))], [scelte, campi])

  async function applica() {
    setLavoro(t('Applico…'))
    setErrore(null)
    let applicati = 0
    const create: string[] = []
    const errori: { device_id: string; error: string }[] = []
    try {
      for (let i = 0; i < scelte.length; i += LOTTO) {
        const items = scelte.slice(i, i + LOTTO).map((p) => ({
          device_id: p.device_id,
          fields: Object.fromEntries(daApplicare(p).map((c) => [
            c.field,
            c.field === 'org_unit_id' || c.field === 'location_id'
              ? (c.entry_id ?? { name: c.proposed })
              : c.proposed,
          ])),
        }))
        const r = await api.medCmmsApply(items, creaVoci)
        applicati += r.applied
        create.push(...r.created_entries)
        errori.push(...r.errors)
        setLavoro(t('Applicati {n} di {m}…', { n: Math.min(i + LOTTO, scelte.length), m: scelte.length }))
      }
      setFatto(create.length
        ? t('Aggiornate {n} schede; create le voci: {v}.', { n: applicati, v: create.join(', ') })
        : t('Aggiornate {n} schede.', { n: applicati }))
      setErroriApplica(errori)
      setRevisione(false)
      setEsito(null)
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(null)
    }
  }

  return (
    <>
      <Card title={t('Il file')}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('L’elenco degli elettromedicali dell’Ingegneria clinica, esportato dal CMMS o da un foglio: Excel o CSV, con una riga di intestazioni. Le colonne si riconoscono dal nome (numero d’inventario, matricola, MAC, IP, nome, modello, codice CND, CIVAB, UDI, reparto, presidio, stanza, date di collaudo e installazione). Il file non si salva: serve solo a proporre le modifiche.')}
        </p>
        <div className="med-opzioni">
          <input ref={input} type="file" accept=".xlsx,.csv,.tsv,.txt" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) void carica(f) }} />
          <button type="button" onClick={() => input.current?.click()} disabled={Boolean(lavoro)}>{t('Scegli il file…')}</button>
          {fogli && fogli.length > 1 && (
            <label className="field" style={{ margin: 0 }}>
              <span className="field__label">{t('Foglio')}</span>
              <select value={foglio} onChange={(e) => { const i = Number(e.target.value); setFoglio(i); void proponi(fogli[i]) }}>
                {fogli.map((f, i) => <option key={f.nome} value={i}>{t('{nome} ({n} righe)', { nome: f.nome, n: f.righe.length })}</option>)}
              </select>
            </label>
          )}
          {lavoro && <span className="field__hint">{lavoro}</span>}
        </div>
      </Card>
      <ErrorBanner error={errore} />
      {fatto && <p className="field__hint" style={{ color: 'var(--ok)' }}>{fatto}</p>}
      {erroriApplica.length > 0 && (
        <Card title={t('Schede non aggiornate')}>
          <DataTable rows={erroriApplica} rowKey={(e, i) => `${e.device_id}-${i}`} paginazione={false} strumenti={false}
            columns={[
              { key: 'device', header: t('Dispositivo'), value: (e) => e.device_id,
                render: (e) => <Link to={`/medical/devices/${e.device_id}`}>{t('apri')}</Link> },
              { key: 'error', header: t('Motivo'), value: (e) => e.error },
            ]} />
        </Card>
      )}
      {esito && (
        <>
          <div className="wazuh-kpi">
            <StatCard label={t('Righe lette')} value={esito.rows} />
            <StatCard label={t('Abbinate a una scheda')} value={esito.matched} tone="ok" />
            <StatCard label={t('Con qualcosa da cambiare')} value={esito.proposals.length} />
            <StatCard label={t('Non abbinate')} value={esito.unmatched_count} tone={esito.unmatched_count ? 'warn' : 'ok'} />
            {esito.conflicts.length > 0 && <StatCard label={t('In conflitto')} value={esito.conflicts.length} tone="error" />}
          </div>
          <p className="field__hint">{t('Colonne riconosciute: {c}.', { c: esito.columns.join(', ') })}</p>

          {esito.proposals.length > 0 && (
            <Card title={t('Le proposte')}>
              <h4 className="form__sezione">{t('Quali campi applicare')}</h4>
              <div className="form-toggles" role="group" aria-label={t('Quali campi applicare')}>
                {presenti.map((k) => {
                  const on = campi.has(k)
                  return (
                    <button key={k} type="button" className={on ? '' : 'secondary'} aria-pressed={on}
                            onClick={() => { const n = new Set(campi); if (on) n.delete(k); else n.add(k); setCampi(n) }}>
                      {etichetta(k)}
                    </button>
                  )
                })}
              </div>
              <div className="checkbox" style={{ marginTop: 12 }}>
                <input id="cmms-crea" type="checkbox" checked={creaVoci} onChange={(e) => setCreaVoci(e.target.checked)} />
                <label htmlFor="cmms-crea">{t('Crea i reparti e le sedi che mancano nelle anagrafiche')}
                  <small>{t('Senza, i reparti e le sedi che l’elenco nomina e le anagrafiche non hanno restano fuori.')}</small></label>
              </div>
              <DataTable
                id="medical-cmms"
                rows={esito.proposals}
                rowKey={(p) => p.device_id}
                initialSort={{ key: 'device', dir: 'asc' }}
                conteggio={{ nome: t('schede') }}
                selezione={{ chiavi: scelti, onCambia: setScelti, id: (p) => p.device_id }}
                columns={[
                  { key: 'device', header: t('Dispositivo'), value: (p) => p.device_name,
                    render: (p) => <Link to={`/medical/devices/${p.device_id}`}>{p.device_name || '—'}</Link> },
                  { key: 'row', header: t('Riga'), type: 'number', align: 'right', value: (p) => p.row },
                  { key: 'matched_by', header: t('Abbinata per'), filter: 'select', value: (p) => etichettaChiave(p.matched_by) },
                  {
                    key: 'changes', header: t('Cosa cambia'),
                    value: (p) => p.changes.map((c) => `${etichetta(c.field)} ${c.proposed}`).join(' '),
                    render: (p) => (
                      <ul className="med-cambi">
                        {p.changes.map((c) => (
                          <li key={c.field} className={daApplicare(p).includes(c) ? undefined : 'text-muted'}>
                            {etichetta(c.field)}: <span className="text-muted">{c.current || '—'}</span> → <strong>{c.proposed}</strong>
                            {c.new_entry && <span className="chip" style={{ marginLeft: 6 }}>{t('nuova voce')}</span>}
                          </li>
                        ))}
                      </ul>
                    ),
                  },
                ]}
              />
              <div className="form-dialog__azioni">
                <button type="button" onClick={() => setRevisione(true)} disabled={scelte.length === 0 || Boolean(lavoro)}>
                  {t('Rivedi e applica ({n} schede)', { n: scelte.length })}
                </button>
              </div>
            </Card>
          )}

          {esito.conflicts.length > 0 && (
            <Card title={t('Righe in conflitto')}>
              <p className="field__hint" style={{ marginTop: 0 }}>{t('Le chiavi della riga indicano dispositivi diversi (per esempio il numero d’inventario uno, il MAC un altro): non si indovina, si corregge l’elenco o la scheda.')}</p>
              <DataTable rows={esito.conflicts} rowKey={(c) => String(c.row)} paginazione={false} strumenti={false}
                columns={[
                  { key: 'row', header: t('Riga'), type: 'number', value: (c) => c.row },
                  { key: 'keys', header: t('Chi indica ogni chiave'),
                    value: (c) => Object.entries(c.keys).map(([k, v]) => `${etichettaChiave(k)}: ${v}`).join(' · ') },
                ]} />
            </Card>
          )}

          {esito.unmatched.length > 0 && (
            <Card title={t('Righe senza una scheda')}>
              <p className="field__hint" style={{ marginTop: 0 }}>{t('Dispositivi dell’elenco che CENSO non ha riconosciuto in rete, o che nessuna chiave lega a una scheda (nomi uguali su più dispositivi non bastano). Ne sono mostrati al più 500.')}</p>
              <DataTable
                rows={esito.unmatched}
                rowKey={(r) => String(r.row)}
                conteggio={{ nome: t('righe') }}
                nomeEsportazione="cmms-non-abbinate"
                columns={[
                  { key: 'row', header: t('Riga'), type: 'number', value: (r) => Number(r.row) },
                  { key: 'inventory_number', header: t('N. inventario'), mono: true, value: (r) => String(r.inventory_number ?? '') },
                  { key: 'name', header: t('Nome'), value: (r) => String(r.name ?? '') },
                  { key: 'brand', header: t('Produttore'), value: (r) => String(r.brand ?? '') },
                  { key: 'model', header: t('Modello'), value: (r) => String(r.model ?? '') },
                  { key: 'serial_number', header: t('Numero di serie'), mono: true, value: (r) => String(r.serial_number ?? '') },
                  { key: 'mac', header: t('MAC'), mono: true, value: (r) => String(r.mac ?? '') },
                  { key: 'ip', header: t('Indirizzo IP'), mono: true, value: (r) => String(r.ip ?? '') },
                  { key: 'org_unit', header: t('Reparto'), value: (r) => String(r.org_unit ?? '') },
                ]}
              />
            </Card>
          )}
        </>
      )}

      {revisione && (
        <Finestra titolo={t('Applica le proposte')} onChiudi={() => setRevisione(false)}>
          <div className="form-dialog">
            <p>{t('Si aggiornano {n} schede, con {m} modifiche in tutto. Ogni scheda passa dagli stessi controlli di una modifica a mano; una che non li supera resta com’è e lo si dice.', {
              n: scelte.length, m: scelte.reduce((s, p) => s + daApplicare(p).length, 0) })}</p>
            {nuoveVoci.length > 0 && <p>{t('Si creano nelle anagrafiche: {v}.', { v: nuoveVoci.join(', ') })}</p>}
            {campi.has('model') && <p className="field__hint">{t('Un modello preso dall’elenco resta come uno scritto a mano: la sincronizzazione oraria non lo cambia.')}</p>}
            {lavoro && <p className="field__hint">{lavoro}</p>}
            <div className="form-dialog__azioni">
              <button type="button" className="secondary" onClick={() => setRevisione(false)} disabled={Boolean(lavoro)}>{t('Indietro')}</button>
              <button type="button" onClick={() => void applica()} disabled={Boolean(lavoro)}>{t('Applica')}</button>
            </div>
          </div>
        </Finestra>
      )}
    </>
  )
}
